import {_, action, memoize, utils} from '@api'
import {CatalogStore, configs, globalStores as stores, onSiteError, ui} from '@stores'
import {synchronizeStores} from '@stores/SynchronizeStores'
import {authStore, guid} from '@utils'
import {CatalogInstanceChildStore} from './CatalogInstanceChildStore'

const DEBUG_IMAGE_SERVICE = DEBUG && false

export interface IUpload {
  files: FileList | IHasFile
  entityIds?: Array<string>
  originalImageId?: string
  isRefetchNeeded?: boolean
  targetIndex?: string
}

type UploadType = 'single' | 'bulk'

export type DataPng = string
export type DataJpg = string
export type DataImg = DataJpg | DataPng | string

export interface IHasFile {
  file: File
}

export interface FileImage extends IHasFile {
  image?: DataImg
}

interface ImageObject extends IHasFile {
  position: number
  image: DataImg
  isLocal: true
}

/**
 * Store for dealing with processing images being dragged/pasted into the catalog
 *
 * Some examples include:
 *    * File(s) from the desktop
 *    * A link to an image from something like google docs
 *    * A link to an image direct from C8
 *    * Pasting an image from the clipboard (screenshot tools, copy image, etc)
 *    * Pasting image file(s) on the user's clipboard
 *
 * Some key flows:
 *    - Drag Image from desktop to Card and drop
 *        -- Drag Type will be set to 'images' by reading data transfer during DRAG {@see DragDrop.readDataTransfer}
 *        -- On drop {@see CatalogMutationsStore.assignImageToCatalogItem} will be called
 *        -- {@see readImageFromDataTransfer} -> {@see readFileImages}
 *        -- We now have an array like:
 *        [{
 *            file: File {name: 'untitled-7.jpg', lastModified: 1635536218000, lastModifiedDate: Fri Oct 29 2021 14:36:58 GMT-0500 (Central Daylight Time), webkitRelativePath: '', size: 6925193, …}
 *            image: {image: 'data:image/jpeg;base64,/9j/4SH9RXhpZgAASUkqAAgAAAA…QLGIZm36lqRaBjfRSdTO/tIdyTZgwAI009fhEC77o9r+1/9k='}
 *         }]
 *         -- TODO - if we ALREADY have an identically named file + size , say so and allow the user to reuse it
 *        -- Create images in C8 - {@see uploadImagesToC8}
 *        --
 *        --
 *        --
 *        --
 *
 *
 *    -
 * Flow for D&D of image on to cards (multiple images):
 *    - Files are read out of data transfer {@see CatalogMutationsStore.assignImageToCatalogItem}
 */
export class CatalogImageService extends CatalogInstanceChildStore {
  constructor(public catalog: CatalogStore) {
    super(catalog)

    synchronizeStores(() => {
    })
  }

  /**
   * User can be dragging in:
   *    * File(s) from the desktop
   *    * A link to an image from something like google docs
   *    * A link to an image direct from C8
   * @param dt
   */
  @action readImageFromDataTransfer = async (dt: DataTransfer, files?: FileList | File): Promise<Array<FileImage>> => {
    /**
     * The logic in this function was repeated throughout the app:
     *
     * {@see DragDropStore.createCatalogItemFromDropImage}
     * {@see CatalogItemSidebarDetailsStore.onDropImage}
     * {@see CatalogItemSidebarDetailsStore.onChangeFileUpload}
     * {@see CatalogItemImageCarouselStore.onImageDrop}
     */

    DEBUG_IMAGE_SERVICE && console.debug(`readImageFromDataTransfer`, {dt, files})
    const {readImageFromURL, readFileImages} = this
    const r = await (
      files instanceof File || files?.length
      ? readFileImages(files)
      : dt.files.length
        // Files are being dragged/pasted
        ? readFileImages(dt.files)
        // Hyperlinks to files are being dragged/pasted
        : readImageFromURL(dt)
    )

    DEBUG_IMAGE_SERVICE && console.debug(`readImageFromDataTransfer - done`, {dt, r})

    return r
  }

  /**
   * Converts file objects into data:uris by reading them individually - expensive!
   * Why this vs resolveImages?
   * * @param files
   */
  readFileImages = async (files: FileList | File): Promise<Array<FileImage>> => {
    DEBUG_IMAGE_SERVICE && console.debug(`readFileImages`, {files})
    return await Promise.all(
      _.map(files instanceof  File ? [files] : files, file => {
        return new Promise<FileImage>((resolve) => {
          const reader = new FileReader()
          reader.onload = (e) => resolve({image: e.target.result as DataImg, file})
          reader.readAsDataURL(file)
        })
      }))
  }

  /**
   * Converts file objects into data:uris by reading them individually - expensive!
   * Why this vs readFileImages?
   * @param files
   * @param count
   * @param changeInCount
   */
  resolveImages = async (files, count = 0, changeInCount = true): Promise<Array<ImageObject>> => {
    DEBUG_IMAGE_SERVICE && console.debug(`resolveImages`, {files, count, changeInCount})
    return await Promise.all(
      _.map(files, (file) => {
        return new Promise<ImageObject>((resolve, reject) => {
          const localFile = file && file.type ? file : file.file
          const ext = localFile.type.split('/')[1]
          const size = localFile.size
          if (size < 2e+7) {
            if (ext === 'png' || ext === 'jpg' || ext === 'jpeg') {
              const reader = new FileReader()
              reader.onload = e => {
                const imageObject = {
                  position: changeInCount ? count++ : 0,
                  image: e.target.result,
                  file: localFile,
                  isLocal: true
                } as ImageObject
                resolve(imageObject)
              }
              reader.readAsDataURL(localFile)
            } else {
              reject(new Error(`Only JPEG/JPG/PNG files are allowed`))
            }
          } else {
            reject(new Error(`Max image size 20MB`))
          }
        })
      }))
  }

  /**
   * Fetches a blob from a given URL
   * @param imageLink
   */
  getImageBlob = async (imageLink: string) => {
    const {stores: {nestJSUrl}} = this
    try {
      DEBUG_IMAGE_SERVICE && console.debug(`getImageBlob`, {imageLink})
      // If nest is proxied through same domain in production, this will allow us to load images from remote domains      
      const response = await fetch(`${nestJSUrl}/image-proxy/view/${encodeURIComponent(imageLink)}`)
      return await response.blob()
    } catch (e) {
      if (e.message) {
        console.error(e)
        throw new Error(`${e.message} Restricted images can not be uploaded`)
      }
    }
  }

  @action dataURLtoFile = (uri: DataImg) => {
    let arr = uri.split(','), mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]), n = bstr.length, u8arr = new Uint8Array(n)

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n)
    }

    const blob = new Blob([u8arr], {type: mime}),
      extension = 'png'

    return new File([blob], `${guid()}.${extension}`, {type: blob.type, lastModified: Date.now()})
  }

  @action convertURIToImageData = async (uri: DataImg) => {
    return new Promise(function (resolve, reject) {
      if (uri == null) return reject()

      const canvas = document.createElement('canvas'),
        context = canvas.getContext('2d'),
        image = new Image()

      image.addEventListener('load', function () {
        canvas.width = image.width
        canvas.height = image.height
        context.drawImage(image, 0, 0, canvas.width, canvas.height)
        resolve(context.getImageData(0, 0, canvas.width, canvas.height))
      }, false)
      image.src = uri
    })
  }

  getImageUrlFromLink = async (file, imageURL?: string): Promise<File> => {
    DEBUG_IMAGE_SERVICE && console.debug(`getImageUrlFromLink`, {file, imageURL})

    const {getImageBlob} = this,
      parser = new DOMParser(),
      html = parser.parseFromString(file, 'text/xml')

    let tag = html.querySelector('img'),
      link: any = '', blob: any = ''

    if (imageURL) {
      link = imageURL
      blob = await this.getImageBlob(link)
    } else if (tag) {
      link = tag.getAttribute('src')
      blob = await getImageBlob(link)
    } else {
      const div = document.createElement('div')
      div.innerHTML = file
      tag = document.querySelector('img')
      link = tag.getAttribute('src')
      const byteArray = new Uint8Array(link)
      blob = new Blob([byteArray], {type: `image/${file?.type?.split('/')[1] ?? 'png'}`})
    }

    if (!_.isEmpty(link)) {
      // tslint:disable-next-line:no-invalid-this
      const extension = blob?.type?.split('/')[1] ?? 'png'
      return new File([blob], `${guid()}.${extension}`, {type: blob.type, lastModified: Date.now()})
    }
    return null
  }

  /**
   * The user may drag images from their computer or they may drag images from another browser tab like google image search
   * This function converts the latter to the former via a local <img> tag
   * @param e
   */
  @action getImageFromDragImage = async (dataTransfer: DataTransfer | { files: FileList }): Promise<FileList | File> => {
    // if (this.uploading || this.success) {
    //   throw new Error('Upload is in progress, Please wait for upload to finish')
    // }

    DEBUG_IMAGE_SERVICE && console.debug(`getImageFromDragImage`, dataTransfer)

    let {files} = dataTransfer
    if (files && files.length > ui.settings.maxImagesPerUpload) {
      throw new Error(`Maximum of ${ui.settings.maxImagesPerUpload} images per upload`)
    }

    let result = null
    if (!(files && files.length)) {
      if (dataTransfer instanceof DataTransfer) {
        // D&D of Image from say Google Image to our app
        let dataImage = dataTransfer.getData('text/html'), imageUrl

        DEBUG_IMAGE_SERVICE && console.debug(`dataTransfer.getData('text/html')`, {dataImage})

        if (_.isEmpty(dataImage)) {
          // D&D of C8 image
          imageUrl = dataTransfer.getData('text/c8-image')
        }

        if (!_.isEmpty(dataImage) || imageUrl) {
          try {
            const image = await this.getImageUrlFromLink(dataImage, imageUrl)
            DEBUG_IMAGE_SERVICE && console.debug(`after getImageUrlFromLink()`, {dataImage, imageUrl, image})

            if (!_.isNull(image)) {
              result = image
            } else {
              throw new Error('Error in uploading file')
            }
          } catch (error) {
            if (_.includes(error.message, 'Failed to fetch')) {
              throw new Error(`Restricted images can not be uploaded`)
            } else {
              throw error
            }
          }
        }
      } else {
        throw new Error(`No files were found and object is not a DataTransfer`)
      }
    } else {
      result = files
    }

    return result
  }

  imageExists = (url) => {
    var http = new XMLHttpRequest()

    http.open('HEAD', url, false)
    http.send()

    return http.status != 404
  }

  readImageFromURL = async (dataTransfer: DataTransfer) => {
    DEBUG_IMAGE_SERVICE && console.debug(`readImageFromURL`, {dataTransfer})
    const {getImageFromDragImage, readFileImages} = this
    try {
      const file = await getImageFromDragImage(dataTransfer)
      if (file) {
        const r = await readFileImages(file)

        DEBUG_IMAGE_SERVICE && console.debug(`readImageFromURL - done`, {dataTransfer, r})

        return r
      } else {
        onSiteError(`Seems like file has restriction to upload`)
      }
    } catch (e) {
      throw new Error(`Error while reading file from LINK`)
    }
  }

  /**
   * Take a list of already read in (via FileReader) images and uploads them
   * @param images
   */
  @action uploadImagesToC8 = async (images: Array<IHasFile>): Promise<Array<{ exId: string }>> => {
    DEBUG_IMAGE_SERVICE && console.debug(`uploadImagesToC8`, {images})
    const {uploadImages, dragDrop} = this

    try {
      //const r: Array<{ exId: string }> = await Promise.all(images.map((image) => this.prepareUploads({files: image})))
      //const r: Array<{ exId: string }> = await uploadImages(images)
      const r: Array<{ exId: string }> = await Promise.all(images.filter(_.identity).map((image) => uploadImages([image])))
      DEBUG_IMAGE_SERVICE && console.debug(`uploadImagesToC8`, {images, r})

      return r
    } catch (err) {
      onSiteError(err)
      dragDrop.reset()
    }
  }

  @action prepareUploads = async ({files, entityIds, originalImageId, isRefetchNeeded, targetIndex}: IUpload) => {
    DEBUG_IMAGE_SERVICE && console.debug(`prepareUploads`, {files, entityIds, originalImageId, isRefetchNeeded, targetIndex})

    const {catalog: {dragDrop, refetch, index}, resolveImages} = this

    try {
      const response = files instanceof FileList ? await resolveImages(files, 0) : Array.isArray(files) ? files : [files]
      const imageUploadResponse = await this.uploadImages(response, entityIds, targetIndex ?? index, originalImageId)

      if (imageUploadResponse?.length > 0 || imageUploadResponse?.exId) {
        isRefetchNeeded && refetch()
        return imageUploadResponse
      } else {
        onSiteError('Error in uploading file empty response')
        dragDrop.reset()
      }
    } catch (e) {
      onSiteError(e)
      dragDrop.reset()
    }
  }

  @action uploadImages = async (images: Array<IHasFile>, entityIds?: Array<string>, index?: string, originalImageId?: string) => {
    DEBUG_IMAGE_SERVICE && console.debug(`uploadImages`, {images, entityIds, index, originalImageId})

    let uploadType: UploadType = 'single',
      error = ''
    const token: string = authStore.accessToken,
      formData = new FormData(),
      firstFile = _.first(images)?.file

    if (!_.isEmpty(entityIds)) {
      _.forEach(images, (image) => {
        formData.append('file', image.file)
      })
      _.forEach(entityIds, (entityId) => {
        formData.append('entityIds[]', entityId)
      })
      formData.set('originalImageId', originalImageId)
      formData.set('index', index ?? this.catalog.index)
    } else {
      if (images.length != 1) {
        throw new Error(`Multiple images were passed to uploadImages but no entity ids were.`)
      }

      if (firstFile) {
        formData.append('data', firstFile)
      }
    }

    try {
      if (_.size(entityIds) > 1) {
        uploadType = 'bulk'
      }
      let url: string = !_.isEmpty(entityIds) ? `${configs.config.graphqlEndpoint}es/image-uploads/${uploadType}` : `${configs.config.graphqlEndpoint}es/image-upload`
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: formData
      })

      if (response.status === 200) {
        let result = await response.json()
        DEBUG_IMAGE_SERVICE && console.debug(`uploadImages - result`, {result, images, entityIds, index, originalImageId})
        result?.exId ? this.catalog.forceC8RenderImageIds.add(result.exId) : result?.forEach(({ updatedImageIds }) => updatedImageIds?.forEach(id => this.catalog.forceC8RenderImageIds.add(id)));

        return result
      } else {
        console.error(`Error uploading file`, response)
        if (!error) {
          error = `Error in uploading file: ${firstFile.name}`
        } else {
          error = `, ${firstFile.name}`
        }
      }
    } catch (e) {
      if (!error) {
        error = `Error in uploading file: ${firstFile.name}`
      } else {
        error = `, ${firstFile.name}`
      }
    }

    if (error) { this.site.onError(error)}
  }

  @action addImagesToOfflineItem = (id: string, images: Array<{ exId }>) => {
    DEBUG_IMAGE_SERVICE && console.debug(`addImagesToOfflineItem`, {id, images})

    const {catalog: {offlineItemsByIndex, index}} = this,
      d = offlineItemsByIndex[index][id]
    _.set(offlineItemsByIndex[index][id], ['images'], [...(d.images || []), ...images].filter(_.identity))
  }

  @action updateOfflineDataImages = async (data) => {
    DEBUG_IMAGE_SERVICE && console.debug(`updateOfflineDataImages`, {data})
    if (data?.images?.length) {
      data['images'] = await this.uploadImagesToC8(data['images'])
    }
    return data
  }
}



// WEBPACK FOOTER //
// ./src/stores/catalog/CatalogImageService.ts